import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const ComplaintStatus = () => {
  const [searchParams] = useSearchParams();
  const initialTracking = searchParams.get("tracking") || "";
  const initialCnic = searchParams.get("cnic") || "";

  const [trackingId, setTrackingId] = useState(initialTracking);
  const [cnic, setCnic] = useState(initialCnic);
  const [searchMode, setSearchMode] = useState("tracking");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

  useEffect(() => {
    if (initialTracking) {
      setSearchMode("tracking");
      const timer = setTimeout(() => {
        handleSearch(new Event("submit"), "tracking");
      }, 500);
      return () => clearTimeout(timer);
    }
    if (initialCnic) {
      setSearchMode("cnic");
      const timer = setTimeout(() => {
        handleSearch(new Event("submit"), "cnic");
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, []);

  const copyTrackingId = (text) => {
    if (!text) {
      toast.warning("No tracking ID to copy");
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => toast.success("Tracking ID copied to clipboard!"))
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("Tracking ID copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy. Please select and copy manually.");
    }
  };

  const viewAttachment = (file) => {
    try {
      if (file.url && file.url.startsWith("data:")) {
        const base64Data = file.url.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: file.fileType || "application/octet-stream",
        });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } else {
        window.open(file.url, "_blank");
      }
    } catch (error) {
      console.error("Error viewing file:", error);
      toast.error("Failed to view file");
    }
  };

  const downloadAttachment = (file) => {
    try {
      if (file.url && file.url.startsWith("data:")) {
        const base64Data = file.url.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: file.fileType || "application/octet-stream",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = file.originalName || file.filename || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
        toast.success("Downloading file...");
      } else {
        window.open(file.url, "_blank");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  const handleSearch = async (e, modeOverride) => {
    e.preventDefault();
    const mode = modeOverride || searchMode;

    if (mode === "tracking") {
      if (!trackingId.trim()) {
        toast.warning("Please enter a tracking ID");
        return;
      }
    } else {
      const cleanCnic = cnic.replace(/[-\s]/g, "");
      if (!cleanCnic || cleanCnic.length !== 13) {
        toast.warning("Please enter a valid 13-digit CNIC");
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      setResults([]);

      if (mode === "tracking") {
        try {
          const response = await axios.get(
            `${API_URL}/api/complaints/tracking/${trackingId.trim()}`,
          );
          setResults([{ ...response.data.data, _itemType: "complaint" }]);
          toast.success("Complaint found!");
          setLoading(false);
          return;
        } catch (complaintErr) {
          if (complaintErr.response?.status === 404) {
            try {
              const response = await axios.get(
                `${API_URL}/api/suggestions/tracking/${trackingId.trim()}`,
              );
              setResults([{ ...response.data.data, _itemType: "suggestion" }]);
              toast.success("Suggestion found!");
              setLoading(false);
              return;
            } catch (suggestionErr) {
              if (suggestionErr.response?.status === 404) {
                setError(
                  "No complaint or suggestion found with this tracking ID",
                );
                toast.error("Tracking ID not found");
              } else {
                setError("Error fetching status. Please try again.");
                toast.error("Error fetching status");
              }
            }
          } else {
            setError("Error fetching status. Please try again.");
            toast.error("Error fetching status");
          }
        }
      } else {
        const cleanCnic = cnic.replace(/[-\s]/g, "");
        let complaintsData = [];
        let suggestionsData = [];

        try {
          const cRes = await axios.get(
            `${API_URL}/api/complaints/cnic/${cleanCnic}`,
          );
          complaintsData = cRes.data.data || [];
        } catch (err) {
          if (err.response?.status !== 404) throw err;
        }

        try {
          const sRes = await axios.get(
            `${API_URL}/api/suggestions/cnic/${cleanCnic}`,
          );
          suggestionsData = sRes.data.data || [];
        } catch (err) {
          if (err.response?.status !== 404) throw err;
        }

        const combined = [
          ...complaintsData.map((c) => ({ ...c, _itemType: "complaint" })),
          ...suggestionsData.map((s) => ({ ...s, _itemType: "suggestion" })),
        ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (combined.length === 0) {
          setError("No complaints or suggestions found with this CNIC");
          toast.error("No records found for this CNIC");
        } else {
          setResults(combined);
          toast.success(`Found ${combined.length} record(s)`);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An unexpected error occurred");
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getStatusTheme = (status) => {
    const themes = {
      pending: {
        bg: "linear-gradient(135deg, #fef3c7, #fde68a)",
        color: "#92400e",
        accent: "#f59e0b",
        label: "Pending Review",
      },
      approved: {
        bg: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
        color: "#065f46",
        accent: "#10b981",
        label: "Approved",
      },
      rejected: {
        bg: "linear-gradient(135deg, #fee2e2, #fecaca)",
        color: "#991b1b",
        accent: "#ef4444",
        label: "Rejected",
      },
      resolved: {
        bg: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
        color: "#1e40af",
        accent: "#3b82f6",
        label: "Resolved",
      },
    };
    return (
      themes[status?.toLowerCase()] || {
        bg: "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
        color: "#374151",
        accent: "#6b7280",
        label: status || "Unknown",
      }
    );
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-PK", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCnicInput = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 13);
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="cs-page">
      <div className="cs-bg-orb cs-orb-1"></div>
      <div className="cs-bg-orb cs-orb-2"></div>
      <div className="cs-bg-orb cs-orb-3"></div>

      <div className="cs-container">
        <Link to="/" className="cs-back-link">
          <span className="cs-back-arrow">←</span>
          <span>Back to Home</span>
        </Link>

        <div className="cs-card">
          {/* Header */}
          <div className="cs-header">
            <div className="cs-header-badge">STATUS TRACKER</div>
            <h1 className="cs-title">Check Submission Status</h1>
            <p className="cs-subtitle">
              Track your complaint or suggestion using your Tracking ID or CNIC
            </p>
          </div>

          {/* Mode Toggle */}
          <div className="cs-toggle-wrap">
            <div className="cs-toggle">
              <button
                type="button"
                className={`cs-toggle-btn ${searchMode === "tracking" ? "active" : ""}`}
                onClick={() => {
                  setSearchMode("tracking");
                  setError(null);
                  setResults([]);
                }}
              >
                <span className="cs-toggle-label">Tracking ID</span>
                <span className="cs-toggle-hint">CMP / SUG</span>
              </button>
              <button
                type="button"
                className={`cs-toggle-btn ${searchMode === "cnic" ? "active" : ""}`}
                onClick={() => {
                  setSearchMode("cnic");
                  setError(null);
                  setResults([]);
                }}
              >
                <span className="cs-toggle-label">CNIC</span>
                <span className="cs-toggle-hint">13 digits</span>
              </button>
              <div
                className={`cs-toggle-slider ${searchMode === "cnic" ? "right" : "left"}`}
              ></div>
            </div>
          </div>

          {/* Search Form */}
          <form className="cs-form" onSubmit={(e) => handleSearch(e)}>
            <div className="cs-input-wrap">
              {searchMode === "tracking" ? (
                <input
                  type="text"
                  className="cs-input"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  placeholder="Enter Tracking ID (e.g., CMP-AB12-CD34)"
                />
              ) : (
                <input
                  type="text"
                  className="cs-input"
                  value={cnic}
                  onChange={(e) => setCnic(formatCnicInput(e.target.value))}
                  placeholder="Enter CNIC (e.g., 12345-1234567-1)"
                  maxLength={15}
                />
              )}
            </div>
            <button type="submit" className="cs-submit" disabled={loading}>
              {loading ? (
                <span className="cs-submit-loading">
                  <span className="cs-spinner"></span>
                  Searching...
                </span>
              ) : (
                "Check Status"
              )}
            </button>
          </form>

          {/* Error */}
          {error && (
            <div className="cs-error">
              <div className="cs-error-bar"></div>
              <div>
                <div className="cs-error-title">No Results</div>
                <div className="cs-error-msg">{error}</div>
              </div>
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="cs-results">
              <div className="cs-results-header">
                <div className="cs-results-count">
                  <span className="cs-results-num">{results.length}</span>
                  <span className="cs-results-text">
                    {results.length === 1 ? "record found" : "records found"}
                  </span>
                </div>
                <div className="cs-results-query">
                  Searched by{" "}
                  <strong>
                    {searchMode === "cnic" ? "CNIC" : "Tracking ID"}
                  </strong>
                </div>
              </div>

              {results.map((result, idx) => {
                const itemType = result._itemType;
                const typeLabel =
                  itemType === "complaint" ? "Complaint" : "Suggestion";
                const theme = getStatusTheme(result.status);

                return (
                  <div
                    key={result._id || idx}
                    className="cs-result-card"
                    style={{ borderLeftColor: theme.accent }}
                  >
                    {/* Card Top */}
                    <div className="cs-card-top">
                      <div className="cs-card-top-left">
                        <div
                          className="cs-type-chip"
                          style={{
                            background:
                              itemType === "complaint"
                                ? "linear-gradient(135deg, #01411C, #1a6b3a)"
                                : "linear-gradient(135deg, #1e40af, #3b82f6)",
                          }}
                        >
                          {typeLabel}
                        </div>
                        <div className="cs-tracking-wrap">
                          <span className="cs-tracking-label">Tracking ID</span>
                          <span className="cs-tracking-value">
                            {result.trackingId}
                          </span>
                          <button
                            type="button"
                            className="cs-copy-btn"
                            onClick={() => copyTrackingId(result.trackingId)}
                          >
                            Copy
                          </button>
                        </div>
                      </div>

                      <div
                        className="cs-status-pill"
                        style={{
                          background: theme.bg,
                          color: theme.color,
                        }}
                      >
                        <span
                          className="cs-status-dot"
                          style={{ background: theme.accent }}
                        ></span>
                        {theme.label}
                      </div>
                    </div>

                    {/* Quick Info Row */}
                    <div className="cs-quick-info">
                      <div className="cs-quick-item">
                        <span className="cs-quick-label">Subject</span>
                        <span className="cs-quick-value">{result.subject}</span>
                      </div>
                      <div className="cs-quick-item">
                        <span className="cs-quick-label">Category</span>
                        <span className="cs-quick-value">
                          {result.category}
                        </span>
                      </div>
                      <div className="cs-quick-item">
                        <span className="cs-quick-label">District</span>
                        <span className="cs-quick-value">
                          {result.district}
                        </span>
                      </div>
                      <div className="cs-quick-item">
                        <span className="cs-quick-label">Submitted</span>
                        <span className="cs-quick-value">
                          {formatDate(result.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Details - Always Visible */}
                    <div className="cs-details">
                      {/* Section: Applicant */}
                      <div className="cs-section">
                        <div className="cs-section-title">
                          Applicant Information
                        </div>
                        <div className="cs-grid">
                          <div className="cs-field">
                            <span className="cs-field-label">Full Name</span>
                            <span className="cs-field-value">
                              {result.citizenName}
                            </span>
                          </div>
                          <div className="cs-field">
                            <span className="cs-field-label">CNIC</span>
                            <span className="cs-field-value">
                              {result.cnic}
                            </span>
                          </div>
                          <div className="cs-field">
                            <span className="cs-field-label">Email</span>
                            <span className="cs-field-value">
                              {result.email}
                            </span>
                          </div>
                          <div className="cs-field">
                            <span className="cs-field-label">Phone</span>
                            <span className="cs-field-value">
                              {result.phone}
                            </span>
                          </div>
                          <div className="cs-field cs-field-full">
                            <span className="cs-field-label">Address</span>
                            <span className="cs-field-value">
                              {result.address || "N/A"}
                            </span>
                          </div>
                          <div className="cs-field">
                            <span className="cs-field-label">District</span>
                            <span className="cs-field-value">
                              {result.district}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Section: Submission */}
                      <div className="cs-section">
                        <div className="cs-section-title">
                          {typeLabel} Details
                        </div>
                        <div className="cs-grid">
                          <div className="cs-field">
                            <span className="cs-field-label">Category</span>
                            <span className="cs-field-value">
                              {result.category}
                            </span>
                          </div>
                          <div className="cs-field">
                            <span className="cs-field-label">Type</span>
                            <span className="cs-field-value">
                              {itemType.charAt(0).toUpperCase() +
                                itemType.slice(1)}
                            </span>
                          </div>
                          <div className="cs-field cs-field-full">
                            <span className="cs-field-label">Subject</span>
                            <span className="cs-field-value">
                              {result.subject}
                            </span>
                          </div>
                          <div className="cs-field cs-field-full">
                            <span className="cs-field-label">Description</span>
                            <p className="cs-field-paragraph">
                              {result.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Section: Timeline */}
                      <div className="cs-section">
                        <div className="cs-section-title">Timeline</div>
                        <div className="cs-timeline">
                          <div className="cs-timeline-item">
                            <div className="cs-timeline-dot"></div>
                            <div className="cs-timeline-content">
                              <span className="cs-timeline-label">
                                Submitted
                              </span>
                              <span className="cs-timeline-value">
                                {formatDate(result.createdAt)}
                              </span>
                            </div>
                          </div>
                          <div className="cs-timeline-item">
                            <div className="cs-timeline-dot"></div>
                            <div className="cs-timeline-content">
                              <span className="cs-timeline-label">
                                Last Updated
                              </span>
                              <span className="cs-timeline-value">
                                {formatDate(result.updatedAt)}
                              </span>
                            </div>
                          </div>
                          {result.resolvedDate && (
                            <div className="cs-timeline-item">
                              <div className="cs-timeline-dot success"></div>
                              <div className="cs-timeline-content">
                                <span className="cs-timeline-label">
                                  Resolved
                                </span>
                                <span className="cs-timeline-value">
                                  {formatDate(result.resolvedDate)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Section: Minister Remarks */}
                      {result.ministerRemarks && (
                        <div className="cs-section">
                          <div className="cs-section-title">
                            Minister Remarks
                          </div>
                          <div className="cs-remarks">
                            "{result.ministerRemarks}"
                          </div>
                        </div>
                      )}

                      {/* Section: Attachments */}
                      {result.attachments && result.attachments.length > 0 && (
                        <div className="cs-section">
                          <div className="cs-section-title">
                            Official Attachments ({result.attachments.length})
                          </div>
                          <div className="cs-attachments">
                            {result.attachments.map((file, index) => (
                              <div key={index} className="cs-attachment">
                                <div className="cs-attachment-info">
                                  <span className="cs-attachment-name">
                                    {file.originalName || file.filename}
                                  </span>
                                  <div className="cs-attachment-meta">
                                    <span className="cs-attachment-size">
                                      {formatFileSize(file.fileSize)}
                                    </span>
                                    {file.description && (
                                      <span className="cs-attachment-desc">
                                        {file.description}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="cs-attachment-actions">
                                  <button
                                    type="button"
                                    className="cs-btn-view"
                                    onClick={() => viewAttachment(file)}
                                  >
                                    View
                                  </button>
                                  <button
                                    type="button"
                                    className="cs-btn-download"
                                    onClick={() => downloadAttachment(file)}
                                  >
                                    Download
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer Info */}
          <div className="cs-footer">
            <div className="cs-footer-title">How to use this page</div>
            <div className="cs-footer-grid">
              <div className="cs-footer-item">
                <span className="cs-footer-num">01</span>
                <span className="cs-footer-text">
                  Enter your <strong>Tracking ID</strong> (starts with{" "}
                  <strong>CMP</strong> for complaints or <strong>SUG</strong>{" "}
                  for suggestions)
                </span>
              </div>
              <div className="cs-footer-item">
                <span className="cs-footer-num">02</span>
                <span className="cs-footer-text">
                  Or switch to <strong>CNIC</strong> mode to see all your
                  submissions at once
                </span>
              </div>
              <div className="cs-footer-item">
                <span className="cs-footer-num">03</span>
                <span className="cs-footer-text">
                  Full details, timeline, remarks, and official attachments are
                  shown for every record
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cs-page {
          position: relative;
          min-height: 100vh;
          background: #0a1f14;
          padding: 40px 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow-x: hidden;
        }

        .cs-bg-orb {
          position: fixed;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.35;
          pointer-events: none;
          z-index: 0;
        }

        .cs-orb-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, #01411C, transparent 70%);
          top: -150px;
          left: -150px;
        }

        .cs-orb-2 {
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, #1a6b3a, transparent 70%);
          bottom: -150px;
          right: -150px;
        }

        .cs-orb-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, #F9A826, transparent 70%);
          top: 40%;
          left: 50%;
          transform: translateX(-50%);
          opacity: 0.15;
        }

        .cs-container {
          position: relative;
          z-index: 1;
          max-width: 1000px;
          margin: 0 auto;
        }

        .cs-back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: rgba(255, 255, 255, 0.85);
          text-decoration: none;
          margin-bottom: 24px;
          padding: 10px 18px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          font-size: 13px;
          font-weight: 500;
          backdrop-filter: blur(10px);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .cs-back-link:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateX(-4px);
          border-color: rgba(249, 168, 38, 0.5);
        }

        .cs-back-arrow {
          font-size: 16px;
          transition: transform 0.3s ease;
        }

        .cs-back-link:hover .cs-back-arrow {
          transform: translateX(-3px);
        }

        .cs-card {
          background: rgba(255, 255, 255, 0.98);
          padding: 44px;
          box-shadow:
            0 30px 80px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.08);
          animation: csFadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes csFadeUp {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .cs-header {
          text-align: center;
          margin-bottom: 36px;
        }

        .cs-header-badge {
          display: inline-block;
          padding: 6px 16px;
          background: linear-gradient(135deg, #01411C, #1a6b3a);
          color: #F9A826;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.15em;
          margin-bottom: 16px;
        }

        .cs-title {
          font-size: 34px;
          font-weight: 800;
          color: #0a1f14;
          margin: 0 0 10px;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }

        .cs-subtitle {
          font-size: 15px;
          color: #6b7a8f;
          margin: 0;
          line-height: 1.6;
        }

        /* Toggle */
        .cs-toggle-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 28px;
        }

        .cs-toggle {
          position: relative;
          display: flex;
          background: #f1f5f9;
          padding: 5px;
          width: 100%;
          max-width: 440px;
          border: 1px solid #e2e8f0;
        }

        .cs-toggle-btn {
          position: relative;
          z-index: 2;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          padding: 10px 16px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.3s ease;
          color: #64748b;
          font-family: inherit;
        }

        .cs-toggle-btn.active {
          color: #0a1f14;
        }

        .cs-toggle-label {
          font-size: 14px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .cs-toggle-hint {
          font-size: 10px;
          font-weight: 500;
          opacity: 0.7;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .cs-toggle-slider {
          position: absolute;
          top: 5px;
          bottom: 5px;
          width: calc(50% - 5px);
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
        }

        .cs-toggle-slider.left {
          transform: translateX(0);
        }

        .cs-toggle-slider.right {
          transform: translateX(100%);
        }

        /* Form */
        .cs-form {
          display: flex;
          gap: 12px;
          margin-bottom: 28px;
          flex-wrap: wrap;
        }

        .cs-input-wrap {
          flex: 1;
          min-width: 240px;
          position: relative;
        }

        .cs-input {
          width: 100%;
          padding: 16px 20px;
          border: 2px solid #e2e8f0;
          font-size: 15px;
          font-weight: 500;
          color: #0a1f14;
          outline: none;
          background: #fafbfc;
          transition: all 0.3s ease;
          box-sizing: border-box;
          font-family: inherit;
          letter-spacing: 0.01em;
        }

        .cs-input:focus {
          border-color: #01411C;
          background: white;
          box-shadow: 0 0 0 4px rgba(1, 65, 28, 0.08);
        }

        .cs-input::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }

        .cs-submit {
          padding: 16px 34px;
          background: linear-gradient(135deg, #01411C, #0a5a2a);
          color: white;
          border: none;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          letter-spacing: 0.01em;
          min-width: 160px;
          font-family: inherit;
          box-shadow: 0 4px 14px rgba(1, 65, 28, 0.3);
          position: relative;
          overflow: hidden;
        }

        .cs-submit::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.15), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }

        .cs-submit:hover:not(:disabled)::before {
          transform: translateX(100%);
        }

        .cs-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(1, 65, 28, 0.4);
        }

        .cs-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cs-submit-loading {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          justify-content: center;
        }

        .cs-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: csSpin 0.8s linear infinite;
        }

        @keyframes csSpin {
          to { transform: rotate(360deg); }
        }

        /* Error */
        .cs-error {
          display: flex;
          gap: 16px;
          padding: 18px 20px;
          background: #fef2f2;
          margin-bottom: 24px;
          animation: csFadeUp 0.4s ease;
        }

        .cs-error-bar {
          width: 4px;
          background: #ef4444;
          flex-shrink: 0;
        }

        .cs-error-title {
          font-size: 14px;
          font-weight: 700;
          color: #991b1b;
          margin-bottom: 4px;
        }

        .cs-error-msg {
          font-size: 13px;
          color: #b91c1c;
          line-height: 1.5;
        }

        /* Results Header */
        .cs-results {
          margin-top: 8px;
        }

        .cs-results-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 18px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
          border: 1px solid #bbf7d0;
        }

        .cs-results-count {
          display: flex;
          align-items: baseline;
          gap: 8px;
        }

        .cs-results-num {
          font-size: 22px;
          font-weight: 800;
          color: #01411C;
          line-height: 1;
        }

        .cs-results-text {
          font-size: 13px;
          font-weight: 600;
          color: #166534;
        }

        .cs-results-query {
          font-size: 12px;
          color: #166534;
          font-weight: 500;
        }

        .cs-results-query strong {
          color: #01411C;
        }

        /* Result Card */
        .cs-result-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-left: 5px solid #01411C;
          padding: 24px;
          margin-bottom: 18px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          animation: csFadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
        }

        .cs-result-card:hover {
          box-shadow: 0 12px 40px rgba(1, 65, 28, 0.1);
          transform: translateY(-2px);
        }

        .cs-card-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 18px;
          flex-wrap: wrap;
        }

        .cs-card-top-left {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .cs-type-chip {
          display: inline-block;
          padding: 5px 14px;
          color: white;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          align-self: flex-start;
        }

        .cs-tracking-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .cs-tracking-label {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .cs-tracking-value {
          font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
          font-size: 13px;
          font-weight: 700;
          color: #01411C;
          background: #f0fdf4;
          padding: 4px 10px;
          border: 1px solid #dcfce7;
        }

        .cs-copy-btn {
          padding: 4px 12px;
          font-size: 11px;
          font-weight: 600;
          color: #01411C;
          background: white;
          border: 1px solid #d1d5db;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .cs-copy-btn:hover {
          background: #f0fdf4;
          border-color: #01411C;
          transform: translateY(-1px);
        }

        .cs-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 16px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }

        .cs-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          animation: csPulse 2s ease-in-out infinite;
        }

        @keyframes csPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.3); }
        }

        /* Quick Info */
        .cs-quick-info {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          padding: 16px;
          background: #f8fafc;
          margin-bottom: 16px;
        }

        .cs-quick-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .cs-quick-label {
          font-size: 10px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .cs-quick-value {
          font-size: 13px;
          font-weight: 600;
          color: #0a1f14;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* Details */
        .cs-details {
          padding-top: 20px;
          border-top: 2px dashed #e2e8f0;
        }

        .cs-section {
          margin-bottom: 24px;
        }

        .cs-section:last-child {
          margin-bottom: 0;
        }

        .cs-section-title {
          font-size: 12px;
          font-weight: 800;
          color: #01411C;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin-bottom: 14px;
          padding-bottom: 8px;
          border-bottom: 2px solid #f0fdf4;
          display: inline-block;
        }

        .cs-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
        }

        .cs-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 10px 14px;
          background: #fafbfc;
          border: 1px solid #f1f5f9;
        }

        .cs-field-full {
          grid-column: 1 / -1;
        }

        .cs-field-label {
          font-size: 10px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .cs-field-value {
          font-size: 14px;
          font-weight: 600;
          color: #0a1f14;
          word-break: break-word;
        }

        .cs-field-paragraph {
          font-size: 14px;
          color: #334155;
          line-height: 1.65;
          margin: 4px 0 0;
          text-align: justify;
        }

        /* Timeline */
        .cs-timeline {
          position: relative;
          padding-left: 24px;
        }

        .cs-timeline::before {
          content: '';
          position: absolute;
          left: 6px;
          top: 8px;
          bottom: 8px;
          width: 2px;
          background: linear-gradient(180deg, #01411C, #bbf7d0);
        }

        .cs-timeline-item {
          position: relative;
          display: flex;
          gap: 14px;
          padding: 10px 0;
        }

        .cs-timeline-dot {
          position: absolute;
          left: -24px;
          top: 14px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: white;
          border: 3px solid #01411C;
          box-shadow: 0 0 0 4px #f0fdf4;
        }

        .cs-timeline-dot.success {
          border-color: #10b981;
          box-shadow: 0 0 0 4px #d1fae5;
        }

        .cs-timeline-content {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .cs-timeline-label {
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .cs-timeline-value {
          font-size: 14px;
          font-weight: 600;
          color: #0a1f14;
        }

        /* Remarks */
        .cs-remarks {
          padding: 18px 22px;
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border-left: 4px solid #f59e0b;
          font-size: 14px;
          font-style: italic;
          color: #78350f;
          line-height: 1.7;
        }

        /* Attachments */
        .cs-attachments {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .cs-attachment {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 14px 18px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          transition: all 0.25s ease;
          flex-wrap: wrap;
        }

        .cs-attachment:hover {
          background: #f0fdf4;
          border-color: #bbf7d0;
          transform: translateX(4px);
        }

        .cs-attachment-info {
          flex: 1;
          min-width: 200px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .cs-attachment-name {
          font-size: 14px;
          font-weight: 700;
          color: #0a1f14;
          word-break: break-all;
        }

        .cs-attachment-meta {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .cs-attachment-size {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          background: #f1f5f9;
          padding: 2px 8px;
        }

        .cs-attachment-desc {
          font-size: 11px;
          color: #64748b;
          font-style: italic;
        }

        .cs-attachment-actions {
          display: flex;
          gap: 8px;
        }

        .cs-btn-view,
        .cs-btn-download {
          padding: 8px 18px;
          border: none;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
          letter-spacing: 0.02em;
        }

        .cs-btn-view {
          background: #01411C;
          color: white;
        }

        .cs-btn-view:hover {
          background: #0a5a2a;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(1, 65, 28, 0.3);
        }

        .cs-btn-download {
          background: white;
          color: #01411C;
          border: 1px solid #d1d5db;
        }

        .cs-btn-download:hover {
          background: #f0fdf4;
          border-color: #01411C;
          transform: translateY(-1px);
        }

        /* Footer */
        .cs-footer {
          margin-top: 32px;
          padding: 24px 28px;
          background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
          border: 1px solid #bbf7d0;
        }

        .cs-footer-title {
          font-size: 13px;
          font-weight: 800;
          color: #01411C;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 18px;
        }

        .cs-footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }

        .cs-footer-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .cs-footer-num {
          font-size: 20px;
          font-weight: 800;
          color: #01411C;
          opacity: 0.3;
          line-height: 1;
          flex-shrink: 0;
        }

        .cs-footer-text {
          font-size: 13px;
          color: #166534;
          line-height: 1.6;
        }

        .cs-footer-text strong {
          color: #01411C;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .cs-page {
            padding: 24px 14px;
          }

          .cs-card {
            padding: 26px 20px;
          }

          .cs-title {
            font-size: 24px;
          }

          .cs-subtitle {
            font-size: 13px;
          }

          .cs-form {
            flex-direction: column;
          }

          .cs-submit {
            width: 100%;
          }

          .cs-quick-info {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-grid {
            grid-template-columns: 1fr;
          }

          .cs-footer-grid {
            grid-template-columns: 1fr;
          }

          .cs-card-top {
            flex-direction: column;
            align-items: stretch;
          }

          .cs-status-pill {
            align-self: flex-start;
          }

          .cs-attachment {
            flex-direction: column;
            align-items: stretch;
          }

          .cs-attachment-actions {
            width: 100%;
          }

          .cs-btn-view,
          .cs-btn-download {
            flex: 1;
          }
        }

        @media (max-width: 480px) {
          .cs-results-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .cs-quick-info {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ComplaintStatus;
